import { useEffect, useMemo, useState } from 'react';
import warehouseApi, { StockItem, GetItemsParams, PagedMeta } from '../api/warehouseApi';

// Компонент для отображения изображения товара
function StockItemImage({ item }: { item: StockItem }) {
  if (!item.imagePath) {
    return <div className="w-16 h-16 bg-gray-100 border rounded flex items-center justify-center text-xs text-gray-400">Нет фото</div>;
  }
  
  // Преобразуем относительный путь в полный URL
  const imageUrl = item.imagePath.startsWith('http') ? item.imagePath : `${window.location.origin}${item.imagePath}`;
  
  return (
    <img
      src={imageUrl}
      alt=""
      className="w-16 h-16 object-contain bg-white border rounded"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
      }}
    />
  );
}

function AdminWarehousePage() {
  const [items, setItems] = useState<StockItem[]>([]);
  const [meta, setMeta] = useState<PagedMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [stockId, setStockId] = useState<number | undefined>(1);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const params: GetItemsParams = useMemo(() => ({
    search: search || undefined,
    stockId,
    includeInactive,
    page,
    pageSize
  }), [search, stockId, includeInactive, page, pageSize]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await warehouseApi.getItemsPaged(params);
      setItems(res.data);
      setMeta(res.meta);
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, params.stockId, params.includeInactive, params.page, params.pageSize]);

  const resetForm = () => {
    setEditingItem(null);
    setCreating(false);
    setImageFile(null);
    setShowModal(false);
  };

  const onSave = async () => {
    try {
      if (creating && editingItem) {
        const { id, created, updated, ...payload } = editingItem as any;
        const createdItem = await warehouseApi.addItem(payload);
        if (imageFile) {
          await warehouseApi.uploadImage(createdItem.id, imageFile);
        }
      } else if (editingItem) {
        await warehouseApi.updateItem(editingItem.id, editingItem);
        if (imageFile) {
          await warehouseApi.uploadImage(editingItem.id, imageFile);
        }
      }
      resetForm();
      await load();
    } catch (e: any) {
      alert(e?.response?.data || e?.message || 'Ошибка сохранения');
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm('Удалить товар?')) return;
    try {
      await warehouseApi.deleteItem(id);
      await load();
    } catch (e: any) {
      alert(e?.response?.data || e?.message || 'Ошибка удаления');
    }
  };

  const startCreate = () => {
    setCreating(true);
    setEditingItem({
      id: 0,
      stockId: 1,
      code: '',
      name: '',
      brand: '',
      price: 0,
      quantity: 0,
      deliveryDate: null,
      description: '',
      isActive: true,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      discountPercentage: null,
      newUntilDate: null,
    });
    setShowModal(true);
  };

  return (
    <div className="flex-1 p-4 md:p-6 admin-form">
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Склад: товары</h1>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col">
            <label className="text-sm text-gray-500">Поиск</label>
            <input value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} className="border rounded px-2 py-1" placeholder="Код / Наименование / Бренд" />
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-gray-500">ID склада</label>
            <input type="number" value={stockId ?? ''} onChange={e => { setPage(1); setStockId(e.target.value ? Number(e.target.value) : undefined); }} className="border rounded px-2 py-1 w-28" />
          </div>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={includeInactive} onChange={e => { setPage(1); setIncludeInactive(e.target.checked); }} />
            Показать неактивные
          </label>
          <button onClick={startCreate} className="ml-auto bg-black text-white rounded px-3 py-2">Добавить товар</button>
        </div>

        <div className="border rounded">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Склад</th>
                  <th className="px-3 py-2 text-left">Код</th>
                  <th className="px-3 py-2 text-left">Изображение</th>
                  <th className="px-3 py-2 text-left">Название</th>
                  <th className="px-3 py-2 text-left">Бренд</th>
                  <th className="px-3 py-2 text-left">Цена</th>
                  <th className="px-3 py-2 text-left">Кол-во</th>
                  <th className="px-3 py-2 text-left">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td className="px-3 py-4" colSpan={8}>Загрузка...</td></tr>
                )}
                {error && !loading && (
                  <tr><td className="px-3 py-4 text-red-600" colSpan={8}>{error}</td></tr>
                )}
                {!loading && !error && items.map(i => (
                  <tr key={i.id} className="border-t">
                    <td className="px-3 py-2">{i.id}</td>
                    <td className="px-3 py-2">{i.stockId}</td>
                    <td className="px-3 py-2">{i.code}</td>
                    <td className="px-3 py-2">
                      <StockItemImage item={i} />
                    </td>
                    <td className="px-3 py-2">{i.name}</td>
                    <td className="px-3 py-2">{i.brand}</td>
                    <td className="px-3 py-2">{i.price}</td>
                    <td className="px-3 py-2">{i.quantity}</td>
                    <td className="px-3 py-2 flex gap-2">
                      <button className="px-2 py-1 border rounded" onClick={() => { setEditingItem(i); setCreating(false); setImageFile(null); setShowModal(true); }}>Редактировать</button>
                      <button className="px-2 py-1 border rounded" onClick={() => onDelete(i.id)}>Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 p-3 border-t">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-2 py-1 border rounded">Назад</button>
            <div>Стр. {meta?.page ?? page} из {meta?.totalPages ?? 1}</div>
            <button disabled={!meta?.hasNextPage} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded">Вперед</button>
            <div className="ml-auto flex items-center gap-2">
              <span>На странице</span>
              <select value={pageSize} onChange={e => { setPage(1); setPageSize(Number(e.target.value)); }} className="border rounded px-2 py-1">
                {[10,20,50,100].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {(showModal && (creating || editingItem)) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white w-full max-w-3xl border rounded p-4 flex flex-col gap-3">
            <h2 className="font-medium">{creating ? 'Добавить товар' : `Редактировать #${editingItem?.id}`}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">ID склада</span>
                <input type="number" value={editingItem?.stockId ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, stockId: Number(e.target.value) }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Код</span>
                <input value={editingItem?.code ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, code: e.target.value }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Название</span>
                <input value={editingItem?.name ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, name: e.target.value }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Бренд</span>
                <input value={editingItem?.brand ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, brand: e.target.value }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Цена</span>
                <input type="number" step="0.01" value={editingItem?.price ?? 0} onChange={e => setEditingItem(v => v && ({ ...v, price: Number(e.target.value) }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Количество</span>
                <input type="number" value={editingItem?.quantity ?? 0} onChange={e => setEditingItem(v => v && ({ ...v, quantity: Number(e.target.value) }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Скидка, %</span>
                <input type="number" value={editingItem?.discountPercentage ?? 0} onChange={e => setEditingItem(v => v && ({ ...v, discountPercentage: Number(e.target.value) }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Дата новинки до</span>
                <input type="date" value={editingItem?.newUntilDate?.substring(0,10) ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, newUntilDate: e.target.value ? new Date(e.target.value).toISOString() : null }))} className="border rounded px-2 py-1" />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-sm text-gray-500">Описание</span>
                <textarea value={editingItem?.description ?? ''} onChange={e => setEditingItem(v => v && ({ ...v, description: e.target.value }))} className="border rounded px-2 py-1" rows={3} />
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editingItem?.isActive ?? true} onChange={e => setEditingItem(v => v && ({ ...v, isActive: e.target.checked }))} />
                Активен
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="text-sm text-gray-500">Изображение</span>
                <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files?.[0] ?? null)} />
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button className="bg-black text-white rounded px-3 py-2" onClick={onSave}>Сохранить</button>
              <button className="border rounded px-3 py-2" onClick={resetForm}>Отмена</button>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminWarehousePage;


